import toast from 'react-hot-toast';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import useAuth from 'src/hooks/useAuth';
import { useUpdateProfileMutation } from 'src/graphql/idr/user/mutations/updateProfile.generated';
import { useUpdateAvatarMutation } from 'src/graphql/idr/user/mutations/updateAvatar.generated';
import { useDeleteFileMutation } from 'src/graphql/cms/file/mutations/deleteFile.generated';
import { parseErrorMsg } from 'src/utils/errors';
import SmallUpload from 'src/components/dashboard/upload/SmallUpload';
import { EntityType } from 'src/_types/serverTypes';
import { fData } from 'src/utils/formatNumber';
import { formatDate } from 'src/utils/formatDate';
import { ChangeEvent, useCallback } from 'react';
import { validateNC } from 'src/utils/validateNationalCode';
import { numberNormalizer } from 'src/utils/numberNormalizer';

interface ProfileGeneralSettingsInterface {
  onChangePhoneNumber: (phoneNumber: string) => void;
}

const ProfileGeneralSettings = ({
  onChangePhoneNumber,
  ...props
}: ProfileGeneralSettingsInterface) => {
  const { user, initialize } = useAuth();

  const { mutate: updateProfile } = useUpdateProfileMutation();
  const { mutate: updateAvatar } = useUpdateAvatarMutation();
  const { mutate: deleteItem } = useDeleteFileMutation();

  const deleteImageUrl = useCallback(() => {
    if (user?.avatarUrl) {
      deleteItem({ data: { dto: { url: user?.avatarUrl } } });
    }
  }, [user]);

  const onUpdateAvatar = useCallback((url) => {
    updateAvatar(
      { data: { dto: { avatarUrl: url } } },
      {
        onSuccess: () => {
          initialize();
          toast.success('عکس با موفقیت بارگذاری شد');
        },
        onError: () => {
          toast.error('خطا در بارگذاری عکس');
        },
      }
    );
  }, []);

  const onDeleteAvatar = useCallback(() => {
    updateAvatar(
      { data: { dto: { avatarUrl: null } } },
      {
        onSuccess: () => {
          initialize();
          toast.success('عکس با موفقیت حذف شد');
        },
        onError: () => {
          toast.error('خطا در حذف عکس');
        },
      }
    );
  }, []);

  return (
    <Grid container spacing={3} {...props}>
      <Grid item xl={3} lg={4} md={6} xs={12}>
        <Card>
          <CardContent>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  p: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <SmallUpload
                  showError
                  circle
                  id="profile-avatar"
                  maxSize={400000}
                  sx={{
                    width: 100,
                    height: 100,
                    border: (theme) => `1px dashed ${theme.palette.divider}`,
                    borderRadius: '50%',
                  }}
                  entity={EntityType.User}
                  onSettled={(ok, { url }) => {
                    if (ok) {
                      onUpdateAvatar(url);
                      deleteImageUrl();
                    } else {
                      toast.error('خطا در بارگذاری عکس');
                    }
                  }}
                  file={user?.avatarUrl}
                  caption={
                    <Typography
                      variant="caption"
                      sx={{
                        mx: 'auto',
                        display: 'block',
                        textAlign: 'center',
                        color: 'text.secondary',
                      }}
                    >
                      حداکثر{' '}
                      <span style={{ direction: 'ltr' }}>{fData(400000)}</span>
                    </Typography>
                  }
                />
              </Box>
              <Typography
                color="textPrimary"
                sx={{ mt: 1, color: 'text2.black' }}
                variant="subtitle2"
              >
                {user?.fullName || user.phoneNumber}
              </Typography>
            </Box>
          </CardContent>
          <CardActions>
            <Button
              color={user?.avatarUrl ? 'error' : 'primary'}
              fullWidth
              variant="text"
              onClick={user?.avatarUrl ? onDeleteAvatar : null}
            >
              <label
                style={{ cursor: 'pointer' }}
                htmlFor={user?.avatarUrl ? 'no-where' : 'profile-avatar'}
              >
                {user?.avatarUrl ? 'حذف عکس' : 'بارگذاری عکس'}
              </label>
            </Button>
          </CardActions>
        </Card>
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography color="text2.black" variant="subtitle1">
              نقش کاربر
            </Typography>
            <Typography color="text2.black" sx={{ mt: 1 }} variant="body2">
              {user.roleDtos.reduce((roles, role, i) => {
                roles += ` ${i !== 0 ? '/' : ''} ${role.name}`;
                return roles;
              }, '')}
            </Typography>
          </CardContent>
        </Card>
        {user?.oneToLastLoginDateTime && (
          <Card sx={{ mt: 2 }}>
            <CardContent sx={{ py: 1 }}>
              <Typography color="text2.black" variant="body1">
                تاریخ آخرین ورود
              </Typography>
              <Typography color="text2.black" sx={{ mt: 1 }} variant="body2">
                {formatDate(user?.oneToLastLoginDateTime)}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Grid>
      <Grid item xl={9} lg={8} md={6} xs={12}>
        <Formik
          enableReinitialize
          initialValues={{
            nationalCode: user?.nationalCode || '',
            fullName: user?.fullName || '',
            phoneNumber: user?.phoneNumber || '',
          }}
          validationSchema={Yup.object().shape({
            nationalCode: Yup.mixed().test({
              name: 'validateNC',
              message: 'کد‌ملی صحیح نمی‌باشد.',
              test: (value) => {
                if (!value) {
                  return true;
                }
                return validateNC(value);
              },
            }),
            fullName: Yup.string()
              .max(255)
              .required('نام و نام خانوادگی الزامی می‌باشد'),
            phoneNumber: Yup.string()
              .matches(
                new RegExp('^(\\+98|0)?9\\d{9}$'),
                'شماره تماس شما صحیح نمی‌باشد'
              )
              .required('شماره تماس الزامی می‌باشد'),
          })}
          onSubmit={(values, { setSubmitting }): void => {
            updateProfile(
              {
                data: {
                  dto: {
                    fullName: values.fullName,
                    phoneNumber: values.phoneNumber,
                    nationalCode: values.nationalCode,
                  },
                },
              },
              {
                onSuccess: () => {
                  setSubmitting(false);
                  toast.success('مشخصات کاربری با موفقیت ویرایش شد');
                  initialize();
                },
                onError: (err: any) => {
                  toast.error(parseErrorMsg(err));
                  setSubmitting(false);
                },
              }
            );
          }}
        >
          {({
            errors,
            values,
            touched,
            isSubmitting,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
          }): JSX.Element => (
            <form onSubmit={handleSubmit}>
              <Card>
                <Box sx={{ p: 3, color: 'text2.black' }}>
                  <Typography variant="body1">مشخصات فردی</Typography>
                </Box>
                <Divider />
                <Box sx={{ pt: 7 }} />
                <CardContent>
                  <Grid container spacing={4}>
                    <Grid item md={6} xs={12}>
                      <TextField
                        error={Boolean(touched.fullName && errors.fullName)}
                        fullWidth
                        helperText={touched.fullName && errors.fullName}
                        label="نام و نام خانوادگی"
                        name="fullName"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.fullName}
                        variant="outlined"
                        required
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextField
                        error={Boolean(
                          touched.nationalCode && errors.nationalCode
                        )}
                        fullWidth
                        helperText={touched.nationalCode && errors.nationalCode}
                        label="کد ملی"
                        name="nationalCode"
                        onBlur={handleBlur}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          setFieldValue(
                            'nationalCode',
                            numberNormalizer(event.target.value)
                          );
                        }}
                        value={values.nationalCode}
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
                <Box sx={{ pt: 4 }} />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    p: 2,
                    my: 2,
                  }}
                >
                  <Button
                    color="primary"
                    disabled={isSubmitting}
                    type="submit"
                    variant="contained"
                  >
                    بروز‌رسانی
                  </Button>
                </Box>
                <Divider />
                <Grid container sx={{ p: 2, my: 1.5 }}>
                  <Grid item md={6} xs={12}>
                    <TextField
                      error={Boolean(touched.phoneNumber && errors.phoneNumber)}
                      fullWidth
                      helperText={touched.phoneNumber && errors.phoneNumber}
                      label="شماره تماس"
                      name="phoneNumber"
                      onBlur={handleBlur}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setFieldValue(
                          'phoneNumber',
                          numberNormalizer(event.target.value)
                        );
                      }}
                      value={values.phoneNumber}
                      variant="outlined"
                      required
                    />
                  </Grid>
                  <Grid
                    item
                    md={6}
                    xs={12}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    {user.phoneNumber !== values.phoneNumber &&
                      !errors.phoneNumber && (
                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={() =>
                            onChangePhoneNumber(values.phoneNumber)
                          }
                        >
                          دریافت کد فعال‌سازی
                        </Button>
                      )}
                  </Grid>
                </Grid>
              </Card>
            </form>
          )}
        </Formik>
      </Grid>
    </Grid>
  );
};

export default ProfileGeneralSettings;
